// ═══════════════════════════════════════════════════════════════════════════
// core/lib/rutas — adónde volver después del login (Paso 4) y el Club en el
// portal del cliente (Paso 5 de Eventos, 21-sep-2026)
// ═══════════════════════════════════════════════════════════════════════════
// Un link que llega por mail (`/eventos/e1`, la vuelta del checkout de MP con
// `?compra=…`) abierto sin sesión cae al login, y hasta hoy el login mandaba
// al inicio: el destino se perdía. El router guarda el destino en el `state`
// de la entrada de `/login` (`<Navigate replace state={{ volverA }} />`), y al
// aparecer la sesión se vuelve a él — SI ES UNA RUTA INTERNA.
//
// `volverA` sale de `location.pathname + location.search`, o sea de lo que
// escribió quien armó la URL: `https://app//evil.com` da `pathname` `//evil.com`.
// Por eso el validador es el mismo que `functions/lib/renderVerificado.js`
// para las rutas de un informe (copiado, no importado: paquetes distintos):
// relativa, mismo origen SIEMPRE — `//` sería protocol-relative, `:` caza
// `http:`/`javascript:`, y `\` porque el parser WHATWG trata `/\evil.com`
// como protocol-relative. Ese `:` también rechaza una query con hora
// (`?desde=10:00`): hoy ningún link que llega por mail la lleva, y ante la
// duda se vuelve al inicio, no a un destino a medias. Y nunca se vuelve a una
// pantalla de entrada (`/login`, `/registro` serían un bucle; `/setup-password`
// y `/auth/accion` —la de los links de mail de Auth, A3— no tienen sentido
// como destino).
// ═══════════════════════════════════════════════════════════════════════════

/** Las pantallas de entrada: no son un destino después de entrar. */
const PANTALLAS_DE_ENTRADA = Object.freeze(['/login', '/registro', '/setup-password', '/auth/accion'])

/**
 * PURO. La ruta interna a la que volver tras el login, o `fallback`.
 * @param {unknown} volverA   lo que dejó el guard en `location.state.volverA`
 * @param {string} fallback   el destino de siempre (`/` o `/portal`)
 */
export function destinoTrasLogin(volverA, fallback) {
  if (typeof volverA !== 'string' || !volverA.startsWith('/') || volverA.startsWith('//')) return fallback
  if (/[\s:\\]/.test(volverA)) return fallback
  const path = volverA.split('?')[0]
  if (PANTALLAS_DE_ENTRADA.some((p) => path === p || path.startsWith(`${p}/`))) return fallback
  return volverA
}

/**
 * PURO. La ruta interna a la que lleva el `continueUrl` de un link de mail de
 * Auth (Plataforma A3), o `null`. El parámetro viaja en la URL, o sea que lo
 * escribe cualquiera: se parsea con `new URL()`, tiene que ser del MISMO
 * origen que la app, y lo que queda (`pathname + search`) pasa por el mismo
 * `destinoTrasLogin` que cualquier otro "adónde volver". El hash se descarta.
 * @param {unknown} continueUrl
 * @param {unknown} origen   `window.location.origin`
 */
export function rutaDeContinueUrl(continueUrl, origen) {
  if (typeof continueUrl !== 'string' || !continueUrl || typeof origen !== 'string' || !/^https?:\/\/[^/]+$/.test(origen)) return null
  let u
  try { u = new URL(continueUrl) } catch { return null }
  if (u.origin !== origen) return null
  return destinoTrasLogin(`${u.pathname}${u.search}`, null)
}

/** El `state` que un guard le deja a `/login` para poder volver. */
export function estadoParaVolver(location) {
  return { volverA: `${location.pathname}${location.search || ''}` }
}

// ── El Club en el portal del cliente (Paso 5) ───────────────────────────────
// Las páginas del MIEMBRO del Club se montan en los dos árboles con el mismo
// componente: `/eventos/:id` para el staff (bajo `ConsultorRoute`) y
// `/portal/eventos/:id` para el cliente (bajo `ClienteRoute`). Molde vivo:
// `MiCuentaPage`/`UsuariosPage`, montadas en los dos lados. Qué rutas van al
// portal lo declara cada módulo en su `config.js` (`enPortal: true`); acá sólo
// se derivan dos cosas de esa lista: el prefijo que un link interno necesita
// según el árbol en que está montado, y la traducción del link ÚNICO que llega
// por mail (`/eventos/e1?compra=…`) cuando quien lo abre es un cliente.

const PREFIJO_PORTAL = '/portal'

/** PURO. El prefijo de los links internos según dónde está montada la página: el árbol decide, no el usuario. */
export function prefijoDelPortal(pathname) {
  return typeof pathname === 'string' && (pathname === PREFIJO_PORTAL || pathname.startsWith(`${PREFIJO_PORTAL}/`)) ? PREFIJO_PORTAL : ''
}

/** PURO. Una ruta de módulo que se monta también en el portal: `enPortal: true` y no pública. Un solo predicado para el registro y la traducción. */
export function esRutaDelPortal(route) {
  return Boolean(route) && route.enPortal === true && !route.public && typeof route.path === 'string'
}

/**
 * PURO. Las rutas (patrones de react-router, sin `/` inicial) que un módulo
 * declara `enPortal: true`. Es la única fuente: no hay tabla a mano.
 * @param {Array<{ routes?: Array<{ path: string, enPortal?: boolean, public?: boolean }> }>} modules
 */
export function rutasEnPortalDe(modules) {
  const out = []
  for (const mod of modules || []) for (const r of mod.routes || []) if (esRutaDelPortal(r)) out.push(r.path)
  return out
}

// ── La página pública (T10a-7, ADR 2026-09-22 E7 + E11) ─────────────────────
// Una ruta `landing: true` la sirve una function detrás de un rewrite de
// Hosting, no la SPA: se declara como DATO en el registry del módulo (sin
// `component`) con la function que la sirve (`servidaPor`), y de acá se deriva
// lo que se cruza contra `firebase.json` (`rutasDeLanding.js`). Core no nombra
// ninguna function: el dato lo trae el módulo.

/**
 * PURO. Las rutas `landing: true` que declaran los módulos, con su módulo. Es la
 * única fuente: no hay tabla a mano.
 * @param {Array<{ id?: string, routes?: Array<object> }>} modules
 * @returns {Array<{ modulo: string|undefined, route: object }>}
 */
export function rutasDeLandingDe(modules) {
  const out = []
  for (const mod of modules || []) for (const r of mod.routes || []) if (r && r.landing === true) out.push({ modulo: mod.id, route: r })
  return out
}

/**
 * PURO. Adónde va un CLIENTE que cae en una ruta del árbol del staff: si la
 * ruta existe en el portal, a su gemela (con la query intacta: la vuelta del
 * checkout trae `?compra=`); si no, `null` y el llamador decide (`/portal`).
 * Matcher estricto por patrón, nunca `startsWith`: `/eventos/checkin/:id` o
 * `/eventos/preview/:id` no se traducen.
 * @param {string} pathname
 * @param {string} search
 * @param {string[]} rutasEnPortal   lo que devuelve `rutasEnPortalDe`
 * @param {(patron: string, pathname: string) => unknown} matchPath   el de react-router (inyectado: este archivo es puro)
 */
export function rutaEnPortal(pathname, search, rutasEnPortal, matchPath) {
  if (typeof pathname !== 'string' || !pathname.startsWith('/') || prefijoDelPortal(pathname)) return null
  for (const patron of rutasEnPortal || []) {
    if (matchPath({ path: `/${patron}`, end: true }, pathname)) return `${PREFIJO_PORTAL}${pathname}${search || ''}`
  }
  return null
}
